import { useEffect, useState } from 'react';
import { Modal, ModalOverlay } from 'react-aria-components';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { agentService } from '@/services/agentService';
import { brandService } from '@/services/brandService';
import type { Agent, Brand } from '@/types/domain';

interface AddAgentDialogProps {
  onCreated: (agent: Agent) => void;
  onClose: () => void;
}

/** "Dave Roberts" -> "dave.roberts", matching the backend's default and the existing agent ids. */
function idFromName(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '.')
    .replace(/^\.+|\.+$/g, '')
    .slice(0, 40);
}

const inputClass =
  'h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm shadow-xs disabled:opacity-60';

/**
 * Add an agent (admins). The backend generates the 4-digit mobile-app PIN and returns it once;
 * this dialog shows it on a second step so it can be passed on before closing.
 */
export function AddAgentDialog({ onCreated, onClose }: AddAgentDialogProps) {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [name, setName] = useState('');
  const [id, setId] = useState('');
  const [idEdited, setIdEdited] = useState(false);
  const [email, setEmail] = useState('');
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [commission, setCommission] = useState('12.5');
  const [zohoId, setZohoId] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [portalAccess, setPortalAccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ agent: Agent; pin: string } | null>(null);

  useEffect(() => {
    brandService
      .list()
      .then((rows) => setBrands(rows.filter((b) => b.is_active)))
      .catch(() => setBrands([]));
  }, []);

  const effectiveId = idEdited ? id : idFromName(name);
  const hasEmail = email.trim() !== '';

  function toggleBrand(brandName: string) {
    setSelectedBrands((current) =>
      current.includes(brandName) ? current.filter((b) => b !== brandName) : [...current, brandName],
    );
  }

  async function submit() {
    const rate = Number(commission);
    if (!name.trim()) return setError('Enter a name');
    if (!Number.isFinite(rate) || rate < 0 || rate > 100) return setError('Commission must be between 0 and 100%');

    setSaving(true);
    setError(null);
    try {
      const result = await agentService.create({
        name: name.trim(),
        id: effectiveId,
        email: hasEmail ? email.trim().toLowerCase() : null,
        brands: selectedBrands,
        commission_rate: rate / 100,
        is_admin: isAdmin,
        portal_access: hasEmail && portalAccess,
        zoho_id: zohoId.trim() || null,
      });
      setCreated(result);
      onCreated(result.agent);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add the agent');
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalOverlay isDismissable={!created && !saving} onOpenChange={(open) => !open && onClose()} isOpen className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm">
      <Modal className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog className="max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-xl border border-border bg-card shadow-xl">
          <DialogHeader>
            <DialogTitle>{created ? 'Agent added' : 'Add agent'}</DialogTitle>
            <DialogDescription>
              {created
                ? `${created.agent.name} (${created.agent.id}) can now be chosen across Splitfin.`
                : 'A new agent for Splitfin, the mobile app and, with an email and access, the Trade Portal.'}
            </DialogDescription>
          </DialogHeader>

          {created ? (
            <DialogBody className="space-y-3">
              <div className="rounded-lg border border-border bg-muted/50 p-4 text-center">
                <p className="text-xs text-muted-foreground">Mobile app PIN</p>
                <p className="mt-1 font-mono text-3xl font-semibold tracking-[0.3em]">{created.pin}</p>
              </div>
              <p className="text-xs text-muted-foreground">
                Pass this PIN on now — it is not shown again. They sign in to the mobile app with the
                ID <span className="font-medium text-foreground">{created.agent.id}</span> and this PIN
                {created.agent.portal_access ? ', and to the Trade Portal with a link sent to their email.' : '.'}
              </p>
            </DialogBody>
          ) : (
            <DialogBody className="space-y-3">
              {error && (
                <p className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
                  {error}
                </p>
              )}

              <label className="block space-y-1 text-sm">
                <span className="font-medium">Name</span>
                <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Dave Roberts" autoFocus />
              </label>

              <label className="block space-y-1 text-sm">
                <span className="font-medium">ID</span>
                <input
                  className={inputClass}
                  value={effectiveId}
                  onChange={(e) => {
                    setIdEdited(true);
                    setId(e.target.value.toLowerCase());
                  }}
                  placeholder="dave.roberts"
                />
                <span className="block text-xs text-muted-foreground">Used to sign in to the mobile app. Cannot be changed later.</span>
              </label>

              <label className="block space-y-1 text-sm">
                <span className="font-medium">Email</span>
                <input className={inputClass} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" />
              </label>

              <fieldset className="space-y-1 text-sm">
                <legend className="font-medium">Brands</legend>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 sm:grid-cols-3">
                  {brands.map((brand) => (
                    <label key={brand.id} className="flex min-h-8 items-center gap-2">
                      <input type="checkbox" checked={selectedBrands.includes(brand.name)} onChange={() => toggleBrand(brand.name)} />
                      <span className="truncate">{brand.name}</span>
                    </label>
                  ))}
                </div>
                <span className="block text-xs text-muted-foreground">The products they see in the mobile app. None selected means none.</span>
              </fieldset>

              <div className="grid grid-cols-2 gap-3">
                <label className="block space-y-1 text-sm">
                  <span className="font-medium">Commission %</span>
                  <input className={inputClass} type="number" min={0} max={100} step={0.5} value={commission} onChange={(e) => setCommission(e.target.value)} />
                </label>
                <label className="block space-y-1 text-sm">
                  <span className="font-medium">Zoho salesperson ID</span>
                  <input className={inputClass} value={zohoId} onChange={(e) => setZohoId(e.target.value)} placeholder="Optional" />
                </label>
              </div>

              <label className="flex min-h-8 items-center gap-2 text-sm">
                <input type="checkbox" checked={portalAccess && hasEmail} disabled={!hasEmail} onChange={(e) => setPortalAccess(e.target.checked)} />
                <span>Trade Portal access{hasEmail ? '' : ' (add an email first)'}</span>
              </label>
              <label className="flex min-h-8 items-center gap-2 text-sm">
                <input type="checkbox" checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} />
                <span>Admin — every customer and brand, and the Trade Order page</span>
              </label>
            </DialogBody>
          )}

          <DialogFooter>
            {created ? (
              <Button intent="primary" size="sm" onPress={onClose}>
                Done
              </Button>
            ) : (
              <>
                <Button intent="outline" size="sm" onPress={onClose} isDisabled={saving}>
                  Cancel
                </Button>
                <Button intent="primary" size="sm" onPress={() => void submit()} isDisabled={saving || !name.trim()}>
                  {saving && <Loader2 className="size-4 animate-spin" />}
                  Add agent
                </Button>
              </>
            )}
          </DialogFooter>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
